import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiErrorMessage } from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import StatusBadge from "../../components/StatusBadge";
import { formatCLP, formatUSD, formatDateTime } from "../../lib/format";
import { trackEvent } from "../../lib/analytics";

export default function CheckoutReturn() {
    const [params] = useSearchParams();
    const provider = params.get("provider");
    const purchaseId = params.get("purchase_id");
    const subscriptionId = params.get("subscription_id");
    const cancelled = params.get("cancelled") === "1";

    const [purchase, setPurchase] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (provider === "subscription") {
            if (!subscriptionId) { setError("Missing subscription identifier"); setLoading(false); return; }
            (async () => {
                try {
                    if (cancelled) {
                        setError("");
                        setSubscription({ status: "cancelled" });
                    } else {
                        const { data } = await api.get(`/checkout/subscription/verify`, { params: { subscription_id: subscriptionId } });
                        setSubscription(data);
                    }
                } catch (err) {
                    setError(apiErrorMessage(err));
                } finally {
                    setLoading(false);
                }
            })();
            return;
        }
        if (!purchaseId) {
            setError("Missing purchase identifier");
            setLoading(false);
            return;
        }
        (async () => {
            try {
                if (provider === "paypal" && !cancelled) {
                    // Capture the payment
                    const { data } = await api.post("/checkout/paypal/capture", { purchase_id: purchaseId });
                    setPurchase(data);
                } else if (provider === "flow") {
                    // Verify status with Flow
                    const { data } = await api.get(`/checkout/flow/verify`, { params: { purchase_id: purchaseId } });
                    setPurchase(data);
                } else {
                    // Cancelled — just fetch from /me/purchases
                    const { data } = await api.get("/me/purchases");
                    const found = (data || []).find((p) => p.id === purchaseId);
                    setPurchase(found || null);
                }
            } catch (err) {
                setError(apiErrorMessage(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [provider, purchaseId, subscriptionId, cancelled]);

    // Fire analytics events once the purchase status is resolved
    useEffect(() => {
        if (!purchase) return;
        if (purchase.status === "paid") {
            trackEvent("purchase", {
                transaction_id: purchase.id,
                value: purchase.amount_paid,
                currency: purchase.currency,
                payment_method: purchase.payment_method,
            });
        } else if (cancelled || purchase.status === "cancelled") {
            trackEvent("checkout_cancelled", {
                purchase_id: purchase.id,
                payment_method: purchase.payment_method,
            });
        }
    }, [purchase, cancelled]);

    const isPaid = purchase?.status === "paid";
    const isPending = purchase?.status === "pending";
    const isCancelled = purchase?.status === "cancelled" || cancelled;

    return (
        <div className="min-h-screen flex flex-col" data-testid="checkout-return-page">
            <PublicHeader />
            <main className="flex-1 flex items-center justify-center px-6 py-20">
                <div className="panel-elevated max-w-xl w-full p-10">
                    <div className="label-mono mb-4">/ Payment result</div>

                    {loading && (
                        <div className="space-y-3" data-testid="checkout-loading">
                            <div className="animate-pulse-soft text-avx-muted text-sm">Verifying payment status…</div>
                        </div>
                    )}

                    {!loading && error && (
                        <div>
                            <h1 className="font-heading font-black text-3xl tracking-tighter mb-3 text-avx-danger">Error</h1>
                            <p className="text-sm text-avx-muted mb-6">{error}</p>
                            <Link to="/plugins" className="btn-secondary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm inline-block">Back to catalog</Link>
                        </div>
                    )}

                    {!loading && !error && provider === "subscription" && subscription && (
                        <div data-testid={`subscription-status-${subscription.status}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <StatusBadge status={subscription.status} />
                                <span className="label-mono">suscripción</span>
                            </div>
                            {subscription.status === "active" ? (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-success">¡Suscripción activa!</h1>
                                    <p className="text-sm text-avx-muted mb-6">Ya tienes acceso a los plugins del bundle. Se renovará automáticamente al final de cada período.</p>
                                </>
                            ) : subscription.status === "cancelled" ? (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-danger">Suscripción no completada</h1>
                                    <p className="text-sm text-avx-muted mb-6">Cancelaste el proceso en PayPal. Puedes intentarlo de nuevo cuando quieras.</p>
                                </>
                            ) : (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-blue">Procesando suscripción</h1>
                                    <p className="text-sm text-avx-muted mb-6">Estamos esperando la confirmación de PayPal. Se activará automáticamente en unos instantes.</p>
                                </>
                            )}
                            <div className="flex gap-3 flex-wrap">
                                <Link to="/dashboard/subscriptions" className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="goto-subscriptions-btn">
                                    Ver mis suscripciones
                                </Link>
                                <Link to="/dashboard/plugins" className="btn-secondary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                                    Mis plugins
                                </Link>
                            </div>
                        </div>
                    )}

                    {!loading && !error && provider !== "subscription" && purchase && (
                        <div data-testid={`checkout-status-${purchase.status}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <StatusBadge status={purchase.status} />
                                <span className="label-mono">{provider}</span>
                            </div>
                            {isPaid && (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-success">Payment confirmed!</h1>
                                    <p className="text-sm text-avx-muted mb-6">Your plugin is now available for download in your private dashboard.</p>
                                </>
                            )}
                            {isPending && (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-blue">Payment in progress</h1>
                                    <p className="text-sm text-avx-muted mb-6">We are waiting for the provider's confirmation. It will automatically update to "Paid" in a few minutes.</p>
                                </>
                            )}
                            {isCancelled && (
                                <>
                                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2 text-avx-danger">Payment not completed</h1>
                                    <p className="text-sm text-avx-muted mb-6">The transaction was cancelled or declined. You can try again.</p>
                                </>
                            )}

                            <div className="panel p-4 space-y-2 text-sm mb-6">
                                <div className="flex justify-between"><span className="text-avx-muted">Purchase ID</span><span className="font-mono">#{purchase.id.slice(0, 8)}</span></div>
                                <div className="flex justify-between"><span className="text-avx-muted">Amount</span><span className="font-mono text-avx-blue">{purchase.currency === "USD" ? formatUSD(purchase.amount_paid) : formatCLP(purchase.amount_paid)}</span></div>
                                <div className="flex justify-between"><span className="text-avx-muted">Method</span><span className="font-mono uppercase">{purchase.payment_method}</span></div>
                                <div className="flex justify-between"><span className="text-avx-muted">Date</span><span className="font-mono text-xs">{formatDateTime(purchase.created_at)}</span></div>
                            </div>

                            <div className="flex gap-3 flex-wrap">
                                {isPaid && (
                                    <Link to="/dashboard/plugins" className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="goto-plugins-btn">
                                        Go to my plugins
                                    </Link>
                                )}
                                <Link to="/dashboard/purchases" className="btn-secondary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                                    View my purchases
                                </Link>
                                {!isPaid && (
                                    <Link to="/plugins" className="btn-secondary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                                        Back to catalog
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <PublicFooter />
        </div>
    );
}
